<template>
  <div>
    <v-card>
      <v-toolbar dense color="grey lighten-3" flat>
        <v-toolbar-title><v-icon>mdi-attachment</v-icon> Survey Report Attachments</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn text icon @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-layout>
          <v-flex xs12 md4 lg4>
            <span class="body-2">{{ labels.survNo }}: {{ survNoParam }}</span>
          </v-flex>
        </v-layout>
        <v-layout row wrap>
          <form ref="clmFileForm">
            <input type="file" style="display: none" ref="clmFile" multiple @change="doValidateFile" />
          </form>
          <v-text-field
            v-model="filename"
            :label="labels.fileInput"
            readonly
            @click.prevent="
              $refs.clmFileForm.reset()
              $refs.clmFile.click()
            "
            chips
            multiple
            prepend-icon="mdi-file-document"
            :error-messages="fileErrors"
          ></v-text-field>
          <v-btn color="primary" @click="doSave()">
            Attach
            <v-icon right>mdi-plus</v-icon>
          </v-btn>
        </v-layout>
        <v-card> </v-card>
        <v-spacer class="mt-2"></v-spacer>
        <v-card>
          <v-card-text>
            <v-data-table
              :headers="headers"
              :items="items"
              v-bind:options.sync="pagination"
              :server-items-length="totalItems"
              :loading="loading"
              :footer-props="{'items-per-page-options': itemsPerPage }"
              :no-data-text="noDataText"
            >
              <template v-slot:item="row"
                ><tr>
                  <td class="text-xs-left">{{ row.item.survAttachId }}</td>
                  <td class="text-xs-left">{{ row.item.survAttachFileName }}</td>
                  <td class="text-xs-left">{{ formatDate(row.item.createdDate) }}</td>
                  <td class="table-action-header">
                    <table-action
                      :rowData="row"
                      query
                      @query="doQuery(row.item)"
                      remove
                      @remove="doRemove(row.item)"
                      custom
                      customIcon="mdi-download"
                      customText="Download"
                      @custom="doDownload(row.item)"
                    ></table-action>
                  </td></tr
              ></template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-card-text>
      <v-btn class="ma-2" color="error" dark @click.prevent="doExit">
        Exit
        <v-icon dark right>mdi-exit-to-app</v-icon>
      </v-btn>

      <v-dialog v-model="showSurvAttach" width="600px">
        <v-card>
          <v-card-title>
            <span class="headline"> Query File Attachments - {{ this.survNoParam }}</span>
            <v-spacer></v-spacer>
            <v-btn text icon @click="showSurvAttach = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-layout row>
              <v-flex xs6>
                <v-subheader class="queryLabel">Survey Attach Id</v-subheader>
                <div class="queryValue">{{ attachmentInfo.survAttachId }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">Survey Attach File Name</v-subheader>
                <div class="queryValue">{{ attachmentInfo.survAttachFileName }}</div>
              </v-flex>
            </v-layout>
            <v-divider></v-divider>
            <v-layout row>
              <v-flex xs6>
                <v-subheader class="queryLabel">Created By</v-subheader>
                <div class="queryValue">{{ attachmentInfo.createdBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">Created Date</v-subheader>
                <div class="queryValue">{{ formatDisplayDate(attachmentInfo.createdDate) }}</div>
              </v-flex>
            </v-layout>
            <v-layout row>
              <v-flex xs6>
                <v-subheader class="queryLabel">Modified By</v-subheader>
                <div class="queryValue">{{ attachmentInfo.modifiedBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">Modified Date</v-subheader>
                <div class="queryValue">{{ formatDisplayDate(attachmentInfo.modifiedDate) }}</div>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-dialog>

      <confirm ref="confirmDelete">
        <p>Are you sure you want to delete the attachment?</p>
      </confirm>
    </v-card>
  </div>
</template>

<script>
import * as TableConst from '@/mixins/table'
import MessageMixin from '@/mixins/message'
import { expandURI, filterQuery } from '@/utils/ajax'
import { formatDisplayDate } from '@/utils/date'
import { downloadPrompt } from '@/utils/file'
import clone from 'clone'

export default {
  data() {
    return {
      labels: {
        survNo: 'Survey No.',
        fileInput: 'File Input'
      },
      filters: {
        survAttachId: null,
        survNo: null,
        survAttachFileName: null,
        createdDate: null
      },
      headers: [
        { text: 'S/N', value: 'survAttachId' },
        { text: 'File Name', value: 'survAttachFileName' },
        { text: 'Date File Uploaded', value: 'createdDate' },
        { text: 'Action', align: 'center', sortable: false, value: '' }
      ],
      attachmentInfo: {
        survAttachId: '',
        survAttachFileName: '',
        createdBy: '',
        createdDate: '',
        modifiedBy: '',
        modifiedDate: ''
      },
      totalItems: 0,
      file: undefined,
      filename: '',
      showClmAttach: false,
      fileErrors: [],
      rejectList: ['bat', 'com', 'exe', 'js'],
      showSurvAttach: false,
      pagination: {
        sortDesc: [false],
        page: 1
      }
    }
  },
  created() {
    this.fetchData(true)
  },
  methods: {
    async fetchData(options) {
      const apiURI = this.riskSurveyMenu.getSurvAttachList
      if (apiURI) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.pagination
          let expand = {
            page: page - 1,
            size: itemsPerPage,
            options: options
          }
          let massageFilters = clone(this.filters)
          massageFilters.survNo = this.survNoParam
          const result = await this.$http.get(
            filterQuery(expandURI(apiURI, expand), massageFilters, this.pagination.sortBy, this.pagination.sortDesc)
          )
          this.items = []
          if (result.data) {
            const { data } = result.data
            this.totalItems = result.data.totalElements
            if (data) {
              data.map((attach) => {
                const convertList = {
                  survAttachId: attach.survAttachId,
                  survNo: attach.survNo,
                  survAttachFileName: attach.survAttachFileName,
                  createdDate: attach.createdDate
                }
                this.items.push(convertList)
              })
            }
          }
          this.loading = false
        } catch (err) {
          this.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    doExit() {
      this.$emit('close')
    },
    clear() {
      this.file = undefined
      this.filename = ''
      this.fileErrors = []
    },
    doValidateFile(e) {
      this.clear()
      this.resetMessages()
      if (e.target.files[0]) {
        const file = e.target.files[0]
        this.filename = file.name
        if (file.size > 1048576) {
          this.fileErrors = 'File size should be less than 10 MB'
        } else {
          const splitName = file.name.split('.')
          const length = splitName.length
          if (length > 1 && this.rejectList.indexOf(splitName[length - 1].toLowerCase()) > -1) {
            this.fileErrors = 'Files with the following extensions (i.e. js, exe, bat, com) are not allowed.'
          } else {
            this.file = file
          }
        }
      }
    },
    async doSave() {
      if (!this.filename) {
        this.fileErrors = 'Please select file to attach'
        return
      }
      if (!this.file) {
        return
      }
      this.loading = true
      const apiUrl = this.riskSurveyMenu.saveSurvAttach
      if (apiUrl) {
        try {
          let formData = new FormData()
          formData.append('file', this.file)
          formData.append('filename', this.filename)
          formData.append('survNo', this.survNoParam)
          const result = await this.$http.post(apiUrl, formData)
          if (result && result.data) {
            this.fetchData(false)
            this.notify(result.data)
          } else {
            this.$toast.error('Unexpected server response')
          }
        } catch (err) {
          if (err.response && err.response.data) {
            this.notify(err.response.data)
          } else {
            this.$toast.error('Failed to save Risk Survey attachment')
          }
        }
      } else {
        this.$toast.error('missing apiUrl (surveyhandlers)')
      }
      this.clear()
    },
    async doQuery(item) {
      const apiURI = this.riskSurveyMenu.getSurvAttachments
      if (apiURI) {
        const expand = {
          survAttachId: item.survAttachId,
          survNo: this.survNoParam
        }
        try {
          const result = await this.$http.get(expandURI(apiURI, expand))
          const { data } = result.data
          if (data) {
            this.attachmentInfo.survAttachId = data.survAttach.survAttachId
            this.attachmentInfo.survAttachFileName = data.survAttach.survAttachFileName
            this.attachmentInfo.createdBy = data.createdBy
            this.attachmentInfo.createdDate = data.createdDate
            this.attachmentInfo.modifiedBy = data.modifiedBy !== null ? data.modifiedBy : '-'
            this.attachmentInfo.modifiedDate = data.modifiedDate !== null ? data.modifiedDate : '-'
          }
          this.notify(data)
          this.showSurvAttach = true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    async doDownload(item) {
      const apiURI = this.riskSurveyMenu.downloadSurvAttach
      if (apiURI) {
        try {
          const expand = {
            survAttachId: item.survAttachId,
            survNo: this.survNoParam
          }
          const result = await this.$http.get(expandURI(apiURI, expand), {
            responseType: 'blob'
          })
          var headers = result.headers
          var blob = new Blob([result.data], { type: headers['content-type'] })
          var filename = (headers['content-disposition'] || 'file').split('filename=')[1]
          downloadPrompt(blob, filename)
        } catch (err) {
          const { data } = err.response
          if (data) {
            // As the request is requesting blob, error has to be formatted back to json
            try {
              const errUrl = URL.createObjectURL(data)
              const errRes = await this.$http.get(errUrl)
              URL.revokeObjectURL(errUrl)
              if (errRes.data) {
                this.notify(errRes.data)
              }
            } catch (err2) {
            }
          }
        }
      } else {
        this.notify('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async doRemove(item) {
      const prompt = await this.$refs.confirmDelete.doPopUp()
      if (!prompt) return
      const apiURI = this.riskSurveyMenu.deleteSurvAttach
      if (item && apiURI) {
        const expand = {
          survAttachId: item.survAttachId,
          survNo: this.survNoParam
        }
        try {
          const result = await this.$http.delete(expandURI(apiURI, expand))
          const data = result.data
          this.fetchData(false)
          this.notify(data)
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    formatDisplayDate(xDate) {
      return formatDisplayDate(xDate)
    }
  },
  computed: {
    riskSurveyMenu() {
      return this.$store.state.underwriting.specialFuncUWSettings.riskSurveySettings.menu
    },
    riskSurveyTab() {
      return this.$store.state.underwriting.specialFuncUWSettings.riskSurveyTab
    },
    riskSurveyMaster() {
      return this.$store.state.underwriting.specialFuncUWSettings.riskSurveyMaster
    },
    riskSurveyDetails() {
      return this.$store.state.underwriting.specialFuncUWSettings.riskSurveyDetails
    },
    riskSurveyMode() {
      return this.$store.state.underwriting.specialFuncUWSettings.riskSurveyMode
    }
  },
  watch: {
    'pagination': function (value) {
      this.fetchData(false)
    }
  },
  mixins: [TableConst.default, MessageMixin],
  props: {
    survNoParam: {
      type: String,
      required: ''
    }
  }
}
</script>

<style>
</style>
